/**
 * Utility functions for auth/director checkbox determination
 * Consolidates duplicate logic for determining representative info based on checkboxes
 */

import { ShareholderDirector } from "../../../features/merchant/rep";

/**
 * Representative info result
 */
export interface RepresentativeInfo {
  repFirstName: string;
  repLastName: string;
  repTitle: string;
  repEmail: string;
  authChecked: boolean;
  directorChecked: boolean;
}

/**
 * Get director emails from shareholders/directors list
 */
export const getDirectorEmails = (
  shareholdersDirectors: ShareholderDirector[]
): string[] => {
  return shareholdersDirectors
    .map((user) => user?.email)
    .filter((email) => email && email.trim() !== "");
};

/**
 * Check if all directors have emails present
 */
export const areAllDirectorEmailsPresent = (
  shareholdersDirectors: ShareholderDirector[]
): boolean => {
  const directorEmails = getDirectorEmails(shareholdersDirectors);
  return directorEmails.length > 0;
};

/**
 * Determine representative info based on auth/director checkboxes
 * This consolidates the duplicate logic in initializeDraft and resetForm
 *
 * @param params - Parameters for determining representative info
 * @returns Representative info with correctly determined values
 */
export const determineRepresentativeInfo = (params: {
  authChecked: boolean;
  directorChecked: boolean;
  merchantFirstName: string;
  merchantLastName: string;
  merchantTitle: string;
  userEmail: string;
  repEmail: string;
  shareholdersDirectors: ShareholderDirector[];
  initialRepFirstName?: string;
  initialRepLastName?: string;
  initialRepTitle?: string;
}): RepresentativeInfo => {
  const {
    authChecked,
    directorChecked,
    merchantFirstName,
    merchantLastName,
    merchantTitle,
    userEmail,
    repEmail,
    shareholdersDirectors,
    initialRepFirstName = "",
    initialRepLastName = "",
    initialRepTitle = "",
  } = params;

  let resultAuthChecked = authChecked;
  let resultDirectorChecked = directorChecked;
  let repFirstName = initialRepFirstName;
  let repLastName = initialRepLastName;
  let repTitle = initialRepTitle;
  let resultRepEmail = repEmail;

  const directorEmails = getDirectorEmails(shareholdersDirectors);
  const allDirectorsEmailPresent = directorEmails.length > 0;

  // If auth checkbox is checked, use merchant info
  if (resultAuthChecked) {
    repFirstName = merchantFirstName;
    repLastName = merchantLastName;
    repTitle = merchantTitle;
    resultRepEmail = userEmail;
  }
  // If director checkbox is checked and directors exist
  else if (resultDirectorChecked && allDirectorsEmailPresent) {
    const directorInfo = shareholdersDirectors.find(
      (user) => user.email === resultRepEmail
    );

    if (directorInfo) {
      repFirstName = directorInfo.firstName;
      repLastName = directorInfo.lastName;
      repTitle = "Director";
      resultRepEmail = directorInfo.email;
    } else {
      // Director not found, clear fields and uncheck
      repFirstName = "";
      repLastName = "";
      repTitle = "";
      resultRepEmail = "";
      resultDirectorChecked = false;
    }
  } else if (
    !resultAuthChecked &&
    !resultDirectorChecked &&
    repTitle === "Director"
  ) {
    repFirstName = "";
    repLastName = "";
    repTitle = "";
    resultRepEmail = "";
  }

  return {
    repFirstName,
    repLastName,
    repTitle,
    repEmail: resultRepEmail,
    authChecked: resultAuthChecked,
    directorChecked: resultDirectorChecked,
  };
};

/**
 * Determine if auth checkbox was checked based on draft data
 * Used in resetForm to detect if the auth checkbox was originally checked
 */
export const wasAuthCheckboxChecked = (params: {
  userFirstName: string | null;
  userLastName: string | null;
  repEmail: string | null;
  repFirstName: string;
  repLastName: string;
  userEmail: string;
}): boolean => {
  const {
    userFirstName,
    userLastName,
    repEmail,
    repFirstName,
    repLastName,
    userEmail,
  } = params;

  return (
    userFirstName !== null &&
    userLastName !== null &&
    repEmail !== null &&
    repFirstName === userFirstName &&
    repLastName === userLastName &&
    userEmail === repEmail
  );
};

/**
 * Determine if director checkbox was checked based on draft data
 * Used in resetForm to detect if the director checkbox was originally checked
 */
export const wasDirectorCheckboxChecked = (params: {
  shareholdersDirectors: ShareholderDirector[];
  repEmail: string;
}): boolean => {
  const { shareholdersDirectors, repEmail } = params;
  const directorEmails = getDirectorEmails(shareholdersDirectors);
  return directorEmails.includes(repEmail);
};
